import logger from 'ws-scripts/modules/logger';
import { addNewRelicPageAction } from 'ddc-new-relic';
import { LOGGING_ATTR_PREFIX } from '../constants';

export const addPaymentTimeoutToNewRelic = (timeout) => {
	const roundedTimeout = Math.round(timeout);
	addNewRelicPageAction(`${LOGGING_ATTR_PREFIX}-payment-timeout`, {
		timeout: roundedTimeout
	});

	logger.info(
		`${LOGGING_ATTR_PREFIX} -> PAYMENT TIMEOUT: ${roundedTimeout} ms`
	);
};

export const addTotalTimeToNewRelic = (elapsed, typeLabel) => {
	if (elapsed) {
		const roundedElapsed = Math.round(elapsed);
		addNewRelicPageAction(
			`${LOGGING_ATTR_PREFIX}-total-${typeLabel}-time`,
			{
				elapsed: roundedElapsed
			}
		);

		logger.info(
			`${LOGGING_ATTR_PREFIX} -> TOTAL ${typeLabel.toUpperCase()} TIME ELAPSED: ${roundedElapsed} ms`
		);
	}
};

export const addPaymentRenderToNewRelic = (
	renderSource,
	elapsed,
	cacheData = {}
) => {
	const roundedElapsed = Math.round(elapsed);
	const actionName = `${LOGGING_ATTR_PREFIX}-payment-rendered`;

	const payload = {
		elapsed: roundedElapsed,
		renderSource,
		cacheHit: renderSource === 'cache',
		...cacheData
	};

	addNewRelicPageAction(actionName, payload);

	logger.info(
		`${LOGGING_ATTR_PREFIX} -> PAYMENT RENDERED FROM ${renderSource.toUpperCase()}: ${roundedElapsed} ms`,
		payload
	);
};

export const addPaymentMismatchToNewRelic = (mismatchData) => {
	addNewRelicPageAction(`${LOGGING_ATTR_PREFIX}-payment-mismatch`, {
		...mismatchData,
		timestamp: Date.now()
	});

	logger.warn(
		`${LOGGING_ATTR_PREFIX} -> PAYMENT MISMATCH DETECTED`,
		mismatchData
	);
};

// Simple comparison to detect if cached data differs from fresh data
export const comparePaymentData = (cachedData, freshData) => {
	// Just do a simple JSON comparison on the rates objects
	// If they're different, there was a mismatch
	try {
		const cachedRates = cachedData?.rates || {};
		const freshRates = freshData?.rates || {};

		// Quick check - if keys are different, there's a mismatch
		const cachedKeys = Object.keys(cachedRates);
		const freshKeys = Object.keys(freshRates);

		if (
			cachedKeys.length !== freshKeys.length ||
			!cachedKeys.every((key) => freshKeys.includes(key))
		) {
			return { mismatch: true };
		}

		// If stringified rates are different, there's a mismatch
		if (JSON.stringify(cachedRates) !== JSON.stringify(freshRates)) {
			return { mismatch: true };
		}

		return null; // No mismatch
	} catch (error) {
		// If comparison fails, don't report mismatch
		return null;
	}
};

/**
 * Check if user has interacted with wallet
 * @param {Object} walletConfig - Wallet configuration object
 * @returns {boolean} - True if user has interacted
 */
export const hasWalletInteractions = (walletConfig) => {
	if (!walletConfig) return false;

	return !!(
		walletConfig.interactions?.hasOpenedWallet ||
		walletConfig.interactions?.hasUpdatedWallet ||
		walletConfig.interactions?.hasUpdatedNDSCalc ||
		walletConfig.interactions?.paymentEmphasis
	);
};

export const startTimeMarker = (typeLabel) => {
	performance.mark(`start-${typeLabel}-calculation`);
};

export const endTimeMarker = (typeLabel) => {
	performance.mark(`end-${typeLabel}-calculation`);
};

export const getPerformanceMeasure = (typeLabel) => {
	if (
		!performance.getEntriesByName(`start-${typeLabel}-calculation`)
			.length ||
		!performance.getEntriesByName(`end-${typeLabel}-calculation`).length
	) {
		return null;
	}

	performance.measure(
		`${typeLabel}-calculation`,
		`start-${typeLabel}-calculation`,
		`end-${typeLabel}-calculation`
	);

	const measure = performance.getEntriesByName(`${typeLabel}-calculation`)[0];

	performance.clearMarks(`start-${typeLabel}-calculation`);
	performance.clearMarks(`end-${typeLabel}-calculation`);
	performance.clearMeasures(`${typeLabel}-calculation`);

	return measure.duration;
};

export const errorBoundaryHandler = (error, errorInfo) => {
	logger.error(
		`${LOGGING_ATTR_PREFIX}-error-boundary:\n${error}\n${errorInfo.componentStack}`
	);
};
